import { useEffect, useState } from 'react';
import axios from 'axios';
import { Activity } from 'lucide-react';

const API = `${window.location.origin}/api`;

/**
 * Tiny status pill for the admin top bar.
 *
 * Polls /api/health every 60s. Renders:
 *   • green dot — db up, latency < 200ms
 *   • amber dot — db up but slow (>200ms) or status=degraded
 *   • red dot   — request failed (timeout / 5xx)
 *
 * Tooltip shows latency + last-checked timestamp. Failing this pill
 * before users notice is the whole point of /api/health.
 */
export default function HealthStatusBadge() {
  const [state, setState] = useState({ status: 'checking', latency: null });

  useEffect(() => {
    let cancelled = false;
    // Consecutive-failure counter so a single flaky ping doesn't scream
    // "API DOWN" while the server is actually fine (public preview has
    // occasional 8s cold-start latency). Only after 2 back-to-back
    // failures do we downgrade the pill to "down".
    let failStreak = 0;
    const ping = async () => {
      const t0 = performance.now();
      try {
        const r = await axios.get(`${API}/health`, { timeout: 8000 });
        const latency = Math.round(performance.now() - t0);
        failStreak = 0;
        if (!cancelled) setState({
          status: r.data?.status || 'ok',
          latency,
          db: r.data?.db,
          dbLatencyMs: r.data?.db_latency_ms,
        });
      } catch {
        failStreak += 1;
        if (!cancelled) {
          if (failStreak >= 2) {
            setState({ status: 'down', latency: null });
          } else {
            // 1st failure: soft warning, keep last known state hint
            setState((s) => ({ ...s, status: 'degraded' }));
          }
        }
      }
    };
    ping();
    const i = setInterval(ping, 60_000);
    return () => { cancelled = true; clearInterval(i); };
  }, []);

  let color = 'bg-white/30';
  let label = 'Checking…';
  let pulse = '';
  // Thresholds tuned for a real PH-to-server round-trip:
  //   - 200ms is misleadingly fast (only possible on same-region hosting).
  //   - 600ms is comfortably healthy for PH → US-region servers.
  //   - 1500ms+ usually indicates a real backend stall, not just distance.
  // We also display the server-reported db_latency_ms in the tooltip so the
  // owner can tell the difference between "internet is far" and "backend is slow".
  if (state.status === 'ok') {
    if (state.latency != null && state.latency < 600) {
      color = 'bg-[#2E9B50]';
      label = `Healthy · ${state.latency}ms`;
    } else if (state.latency != null && state.latency < 1500) {
      color = 'bg-[#D4AF37]';
      label = `OK · ${state.latency}ms`;
    } else {
      color = 'bg-amber-400';
      label = `Slow · ${state.latency}ms`;
      pulse = 'animate-pulse';
    }
  } else if (state.status === 'degraded') {
    color = 'bg-amber-400'; label = 'Degraded';
    pulse = 'animate-pulse';
  } else if (state.status === 'down') {
    color = 'bg-red-500'; label = 'API down';
    pulse = 'animate-pulse';
  }

  const tooltip = `End-to-end round-trip: ${state.latency ?? '?'}ms` +
    (state.dbLatencyMs != null ? ` · server DB ping: ${state.dbLatencyMs}ms` : '') +
    (state.db ? ` · DB ${state.db}` : '') +
    `\nMost of the round-trip is your own internet distance to the server, not backend speed.`;

  return (
    <div
      className="hidden md:flex items-center gap-1.5 px-2 py-1 border border-white/10 bg-[#0A0A0A] text-[10px] uppercase tracking-wider text-white/60 font-sans cursor-help"
      title={tooltip}
      data-testid="admin-health-badge"
    >
      <span className={`w-2 h-2 rounded-full ${color} ${pulse}`} />
      <Activity className="w-3 h-3" />
      <span className="text-white/60">{label}</span>
    </div>
  );
}
